import { ValidationError } from '@attain-apps/errors'

import type * as extra from '../../extra'
import type * as common from '../common'

import type { Migration } from './migration'
import type { Resource } from './resource'

const EXCLUSION_PREFIX = '-'

export interface Package extends common.Named, common.Described, common.Tagged {
  precursor?: Package
  /**
   * Contains IDs of the packages and resources to deployed by this
   * {@link Package}
   */
  contents: readonly Package.Content[]
  // migrations?: readonly Migration[]
}
export namespace Package {
  /**
   * {@link Package} with its {@link extra.quiver.packages.Id}
   */
  export type WithId = Package & common.Identified<extra.quiver.packages.Id>

  const CONTENT_SEP = ':'
  export type Content<K extends keyof Content.Parsed = keyof Content.Parsed> =
    K extends any
      ? `${'' | typeof EXCLUSION_PREFIX}${K}${typeof CONTENT_SEP}${NonNullable<Content.Parsed[K]>}`
      : never
  export namespace Content {
    /**
     * Creates a {@link Package.Content.Parsed} from {@link Package.Content}
     */
    export function parse<K extends keyof Parsed>(
      content: Content<K>,
    ): Extract<Parsed, Required<Pick<Parsed, K>>>
    export function parse(content: Content): Parsed
    export function parse(content: Content<any>): Parsed {
      for (const key of ['package', 'resource'] satisfies (keyof Parsed)[]) {
        const isExcluded = content.startsWith(EXCLUSION_PREFIX)
        const contentSpecifier = isExcluded
          ? content.substring(EXCLUSION_PREFIX.length)
          : content
        const prefix = `${key}${CONTENT_SEP}`
        if (contentSpecifier.startsWith(prefix)) {
          return {
            isExcluded,
            // NOTE: we just assume/hope the rest of the string is a valid ID
            [key]: contentSpecifier.substring(prefix.length),
          } satisfies Omit<Parsed, typeof key> as Omit<
            Parsed,
            typeof key
          > as Parsed
        }
      }
      throw new ValidationError({
        reason: 'Does not start with any valid prefix',
        type: 'quiver.Package.Content',
        value: content,
      })
    }
    /**
     * @see {@link Package.Content.parse}
     */
    export type Parsed = ExactlyOneOf<{
      readonly package: extra.quiver.packages.Id
      readonly resource: extra.quiver.resources.Id
    }> & {
      readonly isExcluded: boolean
    }
  }
}
